import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { fetchCommonFilters } from '../../api/reports';
import MultiSelect from '../../common/components/select/multi-select';
import { useSetLoading } from '../../common/hooks/appearance';
import {
  generateDefaultMultiSelect,
  generateFiltersExtraData,
  generateFiltersForMultiSelect,
} from '../../common/hooks/data';
import {
  agreementNeedTypeLabels,
  commonMultiSelectFiltersPrefixedData,
} from '../../utils/constant';
import { apiErrorHandler } from '../../utils/handlers';

const CommonFiltersHeader = ({ page }) => {
  const location = useLocation();
  const filterExtraData = useSelector((state) => state?.filters?.filtersExtraData);
  const [filters, setFilters] = useState([]);
  const [applyFilters, setApplyFilters] = useState(false);
  const [commonFilters, setCommonFilters] = useState(commonMultiSelectFiltersPrefixedData);
  const [loading, setLoading] = useState(false);

  const handleFilters = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
    setApplyFilters(true);
  };
  const fetchCommonFiltersData = async () => {
    const commonFiltersData = { ...commonFilters };
    setLoading(true);
    try {
      const res = await fetchCommonFilters({
        ...(page === 'journals' && { journal: 'true' }),
        type: 'all',
      });
      // Entity Processing
      commonFiltersData.entity = [
        ...commonFiltersData.entity,
        ...res.data.data.locations.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // BusinessUnit Processing
      commonFiltersData.businessUnits = [
        ...commonFiltersData.businessUnits,
        ...res.data.data.units.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // Agreement Type Processing
      commonFiltersData.agreementTypes = [
        ...commonFiltersData.agreementTypes,
        ...res.data.data.needs.map((item) => {
          return {
            label: agreementNeedTypeLabels[item?.name] || item?.name,
            value: item.id,
          };
        }),
      ];
      // Sale Type Processing
      commonFiltersData.saleTypes = [
        ...commonFiltersData.saleTypes,
        ...res.data.data.saleTypes.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // Teams Processing
      commonFiltersData.teams = [
        ...commonFiltersData.teams,
        ...res.data.data.teams.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // ReportingManagers Processing
      commonFiltersData.reportingManagers = [
        ...commonFiltersData.reportingManagers,
        ...res.data.data.reportingManagers.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // Store in Local CommonFilterState after Processing
      setCommonFilters(commonFiltersData);
      // Multi Select changes
      setFilters({
        entity: commonFiltersData.entity.map((e) => e.value),
        agreementTypes: commonFiltersData.agreementTypes.map((e) => e.value),
        businessUnits: commonFiltersData?.businessUnits.map((e) => e.value),
        teams: commonFiltersData?.teams.map((e) => e.value),
        reportingManagers: commonFiltersData?.reportingManagers.map((e) => e.value),
        saleTypes: commonFiltersData?.saleTypes.map((e) => e.value),
      });
      generateDefaultMultiSelect({
        entity: commonFiltersData.entity.map((e) => e.value),
        agreementTypes: commonFiltersData.agreementTypes.map((e) => e.value),
        businessUnits: commonFiltersData?.businessUnits.map((e) => e.value),
        teams: commonFiltersData?.teams.map((e) => e.value),
        reportingManagers: commonFiltersData?.reportingManagers.map((e) => e.value),
        saleTypes: commonFiltersData?.saleTypes.map((e) => e.value),
      });
      if (
        location?.state?.globalFilters &&
        location?.state?.redirectionFrom === 'dashboard' &&
        !_.isEqual(location?.state?.globalFilters, commonMultiSelectFiltersPrefixedData)
      ) {
        const redirectedFilters = location?.state?.globalFilters;
        setFilters({ ...redirectedFilters });
        generateFiltersForMultiSelect({
          entity: redirectedFilters?.entity,
          agreementTypes: redirectedFilters?.agreementTypes,
          businessUnits: redirectedFilters?.businessUnits,
          teams: redirectedFilters?.teams,
          reportingManagers: redirectedFilters?.reportingManagers,
          saleTypes: redirectedFilters?.saleTypes,
        });
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };
  useEffect(() => {
    generateFiltersForMultiSelect({
      entity: [],
      agreementTypes: [],
      businessUnits: [],
      teams: [],
      reportingManagers: [],
      saleTypes: [],
    });
    generateFiltersExtraData({
      journal_report_type: '',
    });
    fetchCommonFiltersData();
  }, []);
  useEffect(() => {
    if (applyFilters) {
      // Dispatching Common Filter State to Redux
      generateFiltersForMultiSelect({
        entity: filters?.entity,
        agreementTypes: filters?.agreementTypes,
        businessUnits: filters?.businessUnits,
        teams: filters?.teams,
        reportingManagers: filters?.reportingManagers,
        saleTypes: filters?.saleTypes,
      });
      setApplyFilters(false);
    } else if (location.pathname === '/dashboard-receivables') {
      generateFiltersExtraData({
        journalReportType: 'dashboard-receivables',
        isAgingReport: true,
      });
    }
  }, [
    filters?.entity,
    filters?.agreementTypes,
    filters?.businessUnits,
    filters?.teams,
    filters?.reportingManagers,
    filters?.saleTypes,
    location.pathname,
    applyFilters,
  ]);

  const handleSelectValue = (value) => {
    if (filterExtraData?.journalReportType === 'trustJournal') return { value: '', label: 'NA' };
    return value;
  };

  const isSelectDisabled = () => filterExtraData?.journalReportType === 'trustJournal';

  const handlePlaceholder = () => {
    if (filterExtraData?.journalReportType === 'trustJournal') return 'NA';
    return 'Overall';
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <div className="styling">
      <Row className="entity-styling">
        <Col>
          <MultiSelect
            name="businessUnits"
            formInput={false}
            label="BUSINESS UNIT"
            placeholder="Multiple Selections"
            onChange={(val) => {
              handleFilters('businessUnits', val);
            }}
            value={filters?.businessUnits}
            options={commonFilters?.businessUnits}
            disableSearch
          />
        </Col>
        <Col>
          <MultiSelect
            name="Entity"
            formInput={false}
            placeholder="Multiple Selections"
            label="ENTITY"
            onChange={(val) => {
              handleFilters('entity', val);
            }}
            value={filters?.entity}
            options={commonFilters?.entity}
            disableSearch
          />
        </Col>
        <Col>
          <MultiSelect
            name="agreementTypes"
            formInput={false}
            placeholder="Multiple Selections"
            label="Agreement Need Type"
            onChange={(val) => {
              handleFilters('agreementTypes', val);
            }}
            value={filters?.agreementTypes}
            options={commonFilters?.agreementTypes}
            disableSearch
          />
        </Col>
        {filterExtraData?.isAgingReport && (
          <Col>
            <MultiSelect
              name="saleType"
              formInput={false}
              placeholder="Multiple Selections"
              label="Sale Type"
              disabled={isSelectDisabled()}
              onChange={(val) => {
                handleFilters('saleTypes', val);
              }}
              value={handleSelectValue(filters?.saleTypes)}
              options={commonFilters?.saleTypes}
              disableSearch
            />
          </Col>
        )}
        <Col>
          <MultiSelect
            name="teams"
            formInput={false}
            placeholder="Multiple Selections"
            disabledPlaceholder={handlePlaceholder()}
            label="TEAM"
            disabled={isSelectDisabled()}
            onChange={async (val) => {
              handleFilters('teams', val);
            }}
            value={filters?.teams}
            options={commonFilters?.teams}
            disableSearch
          />
        </Col>
        <Col>
          <MultiSelect
            name="reportingManagers"
            formInput={false}
            placeholder="Multiple Selections"
            disabledPlaceholder={handlePlaceholder()}
            label="REPORTING MANAGER"
            disabled={isSelectDisabled()}
            onChange={(val) => {
              handleFilters('reportingManagers', val);
            }}
            value={filters?.reportingManagers}
            options={commonFilters?.reportingManagers}
            disableSearch
          />
        </Col>
      </Row>
      <hr />
    </div>
  );
};

CommonFiltersHeader.propTypes = {
  page: PropTypes.string,
};

CommonFiltersHeader.defaultProps = {
  page: '',
};

export default CommonFiltersHeader;
